import {
	formatLanguageUrl,
	CODE_REDEEM_PAGE_SLUG,
	BUNDLE_CODE_TYPE_INITIAL,
	PROJECT_CODE_TYPE_INITIAL,
} from 'mobile_web/modules/navigation/routes'

// Redemption deep linking domain, e.g. <projectId>.stqry.link. These QRs do not
// redirect to the app's own origin, so ScanEntry can't validate them by resolving to the app host
// like ordinary links. We accept them by path instead (see matchRedemptionUrl).
const DEEP_LINKING_HOST_SUFFIX = '.stqry.link'

export interface ParsedRedemption {
	code: string
	itemId?: number
	isBundle: boolean
	// Whole-app / app-wide code ('p' initial). The itemId is the project id (linked_id). Mutually
	// exclusive with isBundle; when both are false the target is a collection.
	isProject: boolean
}

// Parse an optional integer item id from a URL path segment or route param. Returns undefined for a
// missing or non-numeric value. Shared with CodeRedeem's route-param parsing so the two agree.
export const parseItemId = (raw?: string): number | undefined => {
	if (!raw) return undefined
	const parsed = parseInt(raw, 10)
	return Number.isFinite(parsed) ? parsed : undefined
}

// Parse a redemption URL into its parts, ignoring any locale/base-path prefix and the host.
// Format: .../code/redeem/<code>/<itemId>/<itemTypeInitial?>  (no initial → collection, 'b' → bundle, 'p' → project)
// Returns undefined for non-redemption URLs so callers can fall back.
export const parseRedemptionUrl = (url: URL): ParsedRedemption | undefined => {
	const segments = url.pathname.split('/').filter(Boolean)
	const idx = segments.findIndex((segment, i) => segment === 'code' && segments[i + 1] === 'redeem')
	if (idx === -1) {
		return undefined
	}
	const code = segments[idx + 2]
	if (!code) {
		return undefined
	}
	let decodedCode: string
	try {
		decodedCode = decodeURIComponent(code)
	} catch {
		// Malformed percent-encoding (e.g. a bare '%') in a scanned/garbage QR — treat as a
		// non-redemption URL rather than letting decodeURIComponent throw out of the scan handler.
		return undefined
	}
	return {
		code: decodedCode,
		itemId: parseItemId(segments[idx + 3]),
		isBundle: segments[idx + 4] === BUNDLE_CODE_TYPE_INITIAL,
		isProject: segments[idx + 4] === PROJECT_CODE_TYPE_INITIAL,
	}
}

// Match a scanned URL as a redemption URL, returning its parsed parts (or undefined). A redemption
// URL is a /code/redeem/... path served from either the app's own host (an ordinary in-app link or a
// resolved shortlink) or the *.stqry.link redemption domain. Unlike other scanned URLs we do NOT
// require it to resolve to the app host — the handcrafted shortlink need not redirect. Returns the
// parsed result so callers needn't re-parse to read it.
export const matchRedemptionUrl = (url: URL): ParsedRedemption | undefined => {
	const fromAllowedHost = url.host === window.location.host || url.hostname.endsWith(DEEP_LINKING_HOST_SUFFIX)
	return fromAllowedHost ? parseRedemptionUrl(url) : undefined
}

// Build the local in-app redemption route (with this app's base path + locale) from parsed parts.
// The scanned URL's own path can't be navigated to directly: it may be on the shortlink host and
// lacks the app's base-path/locale prefix.
export const getCodeRedeemUrl = (parsed: ParsedRedemption): string => {
	// Append only the segments that exist — a missing itemId must not leave an empty path segment
	// (`/code/redeem/<code>//b`), which the router would misparse, sliding the type initial into the
	// itemId slot.
	const segments = [CODE_REDEEM_PAGE_SLUG, encodeURIComponent(parsed.code)]
	if (parsed.itemId !== undefined) {
		segments.push(String(parsed.itemId))
	}
	if (parsed.isBundle) {
		segments.push(BUNDLE_CODE_TYPE_INITIAL)
	} else if (parsed.isProject) {
		segments.push(PROJECT_CODE_TYPE_INITIAL)
	}
	return `${formatLanguageUrl()}/${segments.join('/')}`
}
